import { FC, SetStateAction, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

import { DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Slider } from "~/components/ui/slider";
import { trackEvent } from "~/utils/analytics";

interface SplitSectionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  section: DoclistTreeItemSectionsItem;
  onSplit: (
    sections: [DoclistTreeItemSectionsItem, DoclistTreeItemSectionsItem]
  ) => void;
}

export const SplitSectionDialog: FC<SplitSectionDialogProps> = ({
  open,
  setOpen,
  section,
  onSplit,
}) => {
  const intl = useIntl();
  const [pageValue, setPageValue] = useState(1);
  const [oldSectionName, setOldSectionName] = useState(section.name);
  const [newSectionName, setNewSectionName] = useState("");
  const [oldSectionError, setOldSectionError] = useState(false);
  const [newSectionError, setNewSectionError] = useState(false);

  const handleSliderChange = (newValue: SetStateAction<number>[]) => {
    setPageValue(newValue[0]);
  };
  const handleSliderInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = parseInt(event.target.value, 10);
    if (
      !isNaN(newValue) &&
      newValue >= 1 &&
      newValue <= section.pageCount - 1
    ) {
      setPageValue(newValue);
    }
  };

  const handleBlurOldSection = () => {
    if (!oldSectionName.trim()) {
      setOldSectionError(true);
    } else {
      setOldSectionError(false);
    }
  };

  const handleBlurNewSection = () => {
    if (!newSectionName.trim()) {
      setNewSectionError(true);
    } else {
      setNewSectionError(false);
    }
  };

  const handleSplit = () => {
    trackEvent({
      eventName: "app/doc_list/split_section_dialog/split.button/click",
    });
    const firstSection: DoclistTreeItemSectionsItem = {
      ...section,
      name: oldSectionName,
      pageIds: section.pageIds.slice(0, pageValue),
      pages: section.pages?.slice(0, pageValue),
      pageCount: pageValue,
    };

    const secondSection: DoclistTreeItemSectionsItem = {
      ...section,
      id: "00000000-0000-0000-0000-000000000000",
      name: newSectionName,
      pageIds: section.pageIds.slice(pageValue),
      pages: section.pages?.slice(pageValue) || [],
      pageCount: section.pageCount - pageValue,
    };
    onSplit([firstSection, secondSection]);
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      setPageValue(1);
      setOldSectionName(section.name);
      setNewSectionName("");
      setOldSectionError(false);
      setNewSectionError(false);
    }
  }, [open, section.name]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="min-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="docList.splitSectionDialog.title"
              defaultMessage="Split Document"
            />
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <FormattedMessage
            id="docList.splitSectionDialog.description"
            defaultMessage="Select the page after which the document will be splitted"
          />
        </DialogDescription>
        <div className="flex justify-start items-center gap-4">
          <div className="w-[66px]">
            <Input
              type="number"
              value={pageValue}
              onChange={handleSliderInputChange}
              className="w-[66px]"
              min={1}
              max={section.pageCount - 1}
            />
          </div>
          <Slider
            value={[pageValue]}
            onValueChange={handleSliderChange}
            min={1}
            max={section.pageCount - 1}
            step={1}
            className="grow"
          />
        </div>
        <Separator />
        <div className="flex flex-col gap-2">
          <Label htmlFor="oldSectionName">
            <FormattedMessage
              id="docList.splitSectionDialog.oldSection"
              defaultMessage="First Document Name"
            />
          </Label>
          <Input
            id="oldSectionName"
            type="text"
            value={oldSectionName}
            onChange={(e) => {
              setOldSectionName(e.target.value);
            }}
            required
            onBlur={handleBlurOldSection}
            placeholder={intl.formatMessage({
              id: "docList.splitSectionDialog.placeholder",
              defaultMessage: "Enter document name",
            })}
            className={`${oldSectionError && "border-destructive"} shadow-none`}
          />
          <div className="flex text-sm">
            <span className="text-muted-foreground">{`1-${pageValue} pages`}</span>
            {oldSectionError && (
              <>
                <span className="text-muted-foreground">&nbsp;|&nbsp;</span>
                <span className="text-destructive">
                  <FormattedMessage
                    id="docList.splitSectionDialog.placeholder"
                    defaultMessage="Enter document name"
                  />
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="newSectionName">
            <FormattedMessage
              id="docList.splitSectionDialog.newSection"
              defaultMessage="Second Document Name"
            />
          </Label>
          <Input
            id="newSectionName"
            type="text"
            value={newSectionName}
            onChange={(e) => {
              setNewSectionName(e.target.value);
            }}
            required
            onBlur={handleBlurNewSection}
            placeholder={intl.formatMessage({
              id: "docList.splitSectionDialog.placeholder",
              defaultMessage: "Enter document name",
            })}
            className="shadow-none"
          />
          <div className="flex text-sm">
            <span className="text-muted-foreground text-sm">{`${pageValue + 1}-${section.pageCount} pages`}</span>
            {newSectionError && (
              <>
                <span className="text-muted-foreground">&nbsp;|&nbsp;</span>
                <span className="text-destructive">
                  <FormattedMessage
                    id="docList.splitSectionDialog.placeholder"
                    defaultMessage="Enter document name"
                  />
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                trackEvent({
                  eventName:
                    "app/doc_list/split_section_dialog/cancel.button/click",
                });
                setOpen(false);
              }}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
          </DialogClose>
          <Button
            onClick={handleSplit}
            disabled={!oldSectionName || !newSectionName}
          >
            <FormattedMessage
              id="docList.splitSectionDialog.split"
              defaultMessage="Split"
            />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
